import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';

const LogsTab = () => {
  // State
  const [logs, setLogs] = useState([]);
  const [logFilters, setLogFilters] = useState({
    dateFrom: new Date().toISOString().split('T')[0],
    dateTo: new Date().toISOString().split('T')[0],
    level: '',
    userId: '',
    search: ''
  });
  const [showLogDetails, setShowLogDetails] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [loading, setLoading] = useState(false);

  // Filtrowanie po stronie klienta dla pol nieobslugiwanych przez backend
  // (backend /admin/logs wspiera tylko ?type= i ?limit=)
  const applyClientFilters = (rows, filters) => {
    if (!filters) return rows;
    return rows.filter((log) => {
      if (filters.level && log.level !== filters.level) return false;
      if (filters.userId && String(log.user_id) !== String(filters.userId)) return false;
      const logDate = log.timestamp ? String(log.timestamp).slice(0, 10) : '';
      if (filters.dateFrom && logDate && logDate < filters.dateFrom) return false;
      if (filters.dateTo && logDate && logDate > filters.dateTo) return false;
      if (filters.search) {
        const needle = filters.search.toLowerCase();
        const haystack = `${log.message || ''} ${log.username || ''} ${log.action || ''}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  };

  // Load logs from API
  const loadLogs = useCallback(async (filters) => {
    try {
      setLoading(true);
      const params = {};
      if (filters && filters.level) params.type = filters.level;
      const response = await api.get('/admin/logs', { params });

      const data = response.data;
      const rows = data.logs || data.data?.logs || [];
      setLogs(applyClientFilters(rows, filters));
    } catch (err) {
      console.error('Blad podczas pobierania logow:', err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount
  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // Helper functions
  const getLogLevelColor = (level) => {
    const colors = {
      ERROR: 'var(--danger)',
      WARNING: 'var(--warning)',
      INFO: 'var(--info)',
      DEBUG: 'var(--text-muted)'
    };
    return colors[level] || 'var(--text-muted)';
  };

  const viewLogDetails = (log) => {
    setSelectedLog(log);
    setShowLogDetails(true);
  };

  const copyLogToClipboard = (log) => {
    const logText = `[${log.timestamp}] ${log.level}: ${log.message}`;
    navigator.clipboard.writeText(logText);
  };

  const searchLogs = () => {
    loadLogs(logFilters);
  };

  const refreshLogs = () => {
    loadLogs(logFilters);
  };

  const exportLogs = (logsData) => {
    const rows = logsData || logs;
    if (!rows || rows.length === 0) {
      window.alert('Brak logow do wyeksportowania');
      return;
    }
    const headers = ['id', 'timestamp', 'level', 'username', 'action', 'message'];
    const escape = (v) => {
      const s = v == null ? '' : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const csv = [headers.join(',')]
      .concat(rows.map((l) => headers.map((h) => escape(l[h])).join(',')))
      .join('\n');
    const blob = new Blob([String.fromCharCode(0xfeff) + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logi_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      {/* Filtry logow */}
      <div style={{
        border: '1px solid var(--border)',
        borderRadius: '0.375rem',
        backgroundColor: 'var(--card-bg)',
        padding: '1rem'
      }}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: '600' }}>
          🔍 Filtry logow systemowych
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '500' }}>
              Data od
            </label>
            <input
              type="date"
              value={logFilters.dateFrom}
              onChange={(e) => setLogFilters({...logFilters, dateFrom: e.target.value})}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)',
                fontSize: '12px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '500' }}>
              Data do
            </label>
            <input
              type="date"
              value={logFilters.dateTo}
              onChange={(e) => setLogFilters({...logFilters, dateTo: e.target.value})}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)',
                fontSize: '12px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '500' }}>
              Poziom logu
            </label>
            <select
              value={logFilters.level}
              onChange={(e) => setLogFilters({...logFilters, level: e.target.value})}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)',
                fontSize: '12px'
              }}
            >
              <option value="">Wszystkie poziomy</option>
              <option value="ERROR">Bledy</option>
              <option value="WARNING">Ostrzezenia</option>
              <option value="INFO">Informacje</option>
              <option value="DEBUG">Debug</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '500' }}>
              Uzytkownik
            </label>
            <select
              value={logFilters.userId}
              onChange={(e) => setLogFilters({...logFilters, userId: e.target.value})}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)',
                fontSize: '12px'
              }}
            >
              <option value="">Wszyscy uzytkownicy</option>
              {/* Lista uzytkownikow jest teraz w zakladce Lokalizacje */}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '500' }}>
              Szukaj w logach
            </label>
            <input
              type="text"
              value={logFilters.search}
              onChange={(e) => setLogFilters({...logFilters, search: e.target.value})}
              placeholder="Szukaj tekstu..."
              style={{
                width: '100%',
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)',
                fontSize: '12px'
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'end' }}>
            <button
              onClick={searchLogs}
              style={{
                width: '100%',
                padding: '6px 12px',
                border: '1px solid var(--info)', color: 'var(--info)' ,
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500'
              }}
            >
              🔍 Szukaj
            </button>
          </div>
        </div>

        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={refreshLogs}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--success)', color: 'var(--success)' ,
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            🔄 Odswiez
          </button>
          <button
            onClick={() => exportLogs(logs)}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--info)', color: 'var(--info)' ,
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            📤 Eksportuj logi
          </button>
        </div>
      </div>

      {/* Lista logow */}
      <div style={{
        border: '1px solid var(--border)',
        borderRadius: '0.375rem',
        backgroundColor: 'var(--card-bg)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '1rem',
          backgroundColor: 'var(--bg)',
          borderBottom: '1px solid var(--border)',
          fontWeight: '600',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.5rem'
        }}>
          <span>📋 Logi systemowe</span>
          <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
            Znaleziono: {logs.length} rekordow
          </span>
        </div>

        <div className="admin-table-wrap" style={{ overflow: 'auto', maxHeight: '600px' }}>
          {loading ? (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              color: 'var(--text-muted)'
            }}>
              <p>Ladowanie logow...</p>
            </div>
          ) : logs.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              color: 'var(--text-muted)'
            }}>
              <p>Brak logow spelniajacych kryteria</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: 'var(--bg)', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                    Data/Czas
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>
                    Poziom
                  </th>
                  <th className="col-hide-mobile" style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>
                    Uzytkownik
                  </th>
                  <th className="col-hide-mobile" style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>
                    Akcja
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>
                    Wiadomosc
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>
                    Szczegoly
                  </th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, index) => (
                  <tr key={log.id} style={{ backgroundColor: index % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)' }}>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: '500' }}>
                        {new Date(log.timestamp).toLocaleDateString('pl-PL')}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {new Date(log.timestamp).toLocaleTimeString('pl-PL')}
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', borderBottom: '1px solid var(--border)' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: getLogLevelColor(log.level),
                        color: '#fff' ,
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        fontWeight: '500'
                      }}>
                        {log.level}
                      </span>
                    </td>
                    <td className="col-hide-mobile" style={{ padding: '0.75rem', fontSize: '0.875rem', borderBottom: '1px solid var(--border)' }}>
                      {log.username || 'System'}
                    </td>
                    <td className="col-hide-mobile" style={{ padding: '0.75rem', fontSize: '0.875rem', borderBottom: '1px solid var(--border)' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: 'var(--info-light)',
                        color: 'var(--info)',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem'
                      }}>
                        {log.action}
                      </span>
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      borderBottom: '1px solid var(--border)',
                      maxWidth: '300px'
                    }}>
                      <div style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {log.message}
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>
                      <button
                        onClick={() => viewLogDetails(log)}
                        style={{
                          padding: '4px 10px',
                          border: '1px solid var(--info)', color: 'var(--info)' ,
                          cursor: 'pointer',
                          fontSize: '11px'
                        }}
                      >
                        👁️ Szczegoly
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal szczegolow logu */}
      {showLogDetails && selectedLog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--card-bg)',
            borderRadius: '0.5rem',
            padding: '1.5rem',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '600' }}>
                📋 Szczegoly logu
              </h3>
              <button
                onClick={() => setShowLogDetails(false)}
                style={{
                  padding: '6px 12px',
                  border: '1px solid var(--text-muted)', color: 'var(--text-muted)' ,
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'grid', gap: '1rem' }}>
              <div style={{ padding: '1rem', backgroundColor: 'var(--bg)', borderRadius: '0.375rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem' }}>
                  <div>
                    <strong>ID:</strong> {selectedLog.id}
                  </div>
                  <div>
                    <strong>Timestamp:</strong> {new Date(selectedLog.timestamp).toLocaleString('pl-PL')}
                  </div>
                  <div>
                    <strong>Poziom:</strong>
                    <span style={{
                      marginLeft: '0.5rem',
                      padding: '0.25rem 0.5rem',
                      backgroundColor: getLogLevelColor(selectedLog.level),
                      color: '#fff' ,
                      borderRadius: '0.25rem',
                      fontSize: '0.75rem'
                    }}>
                      {selectedLog.level}
                    </span>
                  </div>
                  <div>
                    <strong>Uzytkownik:</strong> {selectedLog.username || 'System'}
                  </div>
                  <div>
                    <strong>Akcja:</strong> {selectedLog.action}
                  </div>
                  <div>
                    <strong>IP:</strong> {selectedLog.ip_address || 'N/A'}
                  </div>
                </div>
              </div>

              <div style={{ padding: '1rem', backgroundColor: 'var(--warning-light)', borderRadius: '0.375rem' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600' }}>
                  📝 Wiadomosc
                </h4>
                <p style={{ margin: 0, fontSize: '0.875rem', wordBreak: 'break-word' }}>
                  {selectedLog.message}
                </p>
              </div>

              {selectedLog.details && (
                <div style={{ padding: '1rem', backgroundColor: 'var(--info-light)', borderRadius: '0.375rem' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600' }}>
                    🔍 Szczegoly techniczne
                  </h4>
                  <pre style={{
                    margin: 0,
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    backgroundColor: 'var(--bg)',
                    padding: '0.5rem',
                    borderRadius: '0.25rem',
                    overflow: 'auto'
                  }}>
                    {typeof selectedLog.details === 'string' ? selectedLog.details : JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.stack_trace && (
                <div style={{ padding: '1rem', backgroundColor: 'var(--danger-light)', borderRadius: '0.375rem' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600' }}>
                    🚨 Stack Trace
                  </h4>
                  <pre style={{
                    margin: 0,
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    backgroundColor: 'var(--bg)',
                    padding: '0.5rem',
                    borderRadius: '0.25rem',
                    overflow: 'auto'
                  }}>
                    {selectedLog.stack_trace}
                  </pre>
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => copyLogToClipboard(selectedLog)}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid var(--info)', color: 'var(--info)' ,
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  📋 Kopiuj do schowka
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogsTab;
